import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { TOPICS } from '@/data/topics';
import { useLocalized } from '@/hooks/useLocalized';
import { EMPTY_FILTERS, type Filters } from './filters';

interface Props {
  filters: Filters;
  onChange: (next: Filters) => void;
}

export function ProblemFilters({ filters, onChange }: Props) {
  const { t } = useTranslation();
  const { l } = useLocalized();
  const set = <K extends keyof Filters>(key: K, value: Filters[K]) =>
    onChange({ ...filters, [key]: value });

  const isDirty = filters.search !== '' || filters.difficulty !== 'all' || filters.topic !== 'all';

  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
      <div className="relative flex-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted" />
        <input
          value={filters.search}
          onChange={(e) => set('search', e.target.value)}
          placeholder={t('problems.searchPlaceholder')}
          className="input pl-10"
          aria-label={t('common.search')}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 lg:flex">
        <select
          className="select min-w-[7rem]"
          value={filters.difficulty}
          onChange={(e) => set('difficulty', e.target.value as Filters['difficulty'])}
          aria-label={t('problems.filterDifficulty')}
        >
          <option value="all">{t('problems.filterDifficulty')}</option>
          <option value="easy">{t('difficulty.easy')}</option>
          <option value="medium">{t('difficulty.medium')}</option>
          <option value="hard">{t('difficulty.hard')}</option>
        </select>

        <select
          className="select min-w-[7rem]"
          value={filters.topic}
          onChange={(e) => set('topic', e.target.value)}
          aria-label={t('problems.filterTopic')}
        >
          <option value="all">{t('problems.filterTopic')}</option>
          {TOPICS.map((tp) => (
            <option key={tp.slug} value={tp.slug}>{l(tp.title)}</option>
          ))}
        </select>
      </div>

      {isDirty && (
        <button
          onClick={() => onChange(EMPTY_FILTERS)}
          className="btn btn-ghost gap-1.5 self-start rounded-DEFAULT px-3 py-2 text-sm lg:self-auto"
        >
          <X className="h-4 w-4" />
          {t('problems.reset')}
        </button>
      )}
    </div>
  );
}
